import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PasswordTextField from '../components/common/PasswordTextField';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [openSignupEnabled, setOpenSignupEnabled] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>(
    'idle',
  );
  const [resendError, setResendError] = useState<string | null>(null);
  const { register, user, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Reason: honor ?next= so users return to their original destination after sign-up.
  const getSafeNext = React.useCallback((): string | null => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('next');
    if (!raw) return null;
    if (!raw.startsWith('/') || raw.startsWith('//')) return null;
    if (raw.startsWith('/login') || raw.startsWith('/register')) return null;
    return raw;
  }, [location.search]);

  // Reason: when the user arrived here from /join/<secret> (JoinPage
  // redirects unauthenticated invitees to /register?invite=<secret>),
  // capture the invite for the register POST. Backend uses it to stamp
  // attribution on the new User.
  const inviteSecret = React.useMemo<string | null>(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get('invite') || '').trim();
    return raw || null;
  }, [location.search]);

  // Reason: fetch the public feature flag so we know whether to show the
  // 'name' field and whether self-signup is permitted at all.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(API_ENDPOINTS.PUBLIC_CONFIG)
      .then((res) => {
        if (!cancelled) {
          setOpenSignupEnabled(Boolean(res.data?.open_signup_enabled));
        }
      })
      .catch(() => {
        // Default to closed signup on failure.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const next = getSafeNext();
      navigate(next || '/home');
    }
  }, [user, navigate, getSafeNext]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Validation
    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    if (!termsAccepted || !aiConsent) {
      setLocalError('Please accept the required consent options to continue');
      return;
    }

    if (openSignupEnabled && !name.trim()) {
      setLocalError('Please enter your name');
      return;
    }

    try {
      // Normalize email to lowercase for consistent storage
      const normalizedEmail = email.toLowerCase().trim();
      const result = await register(
        normalizedEmail,
        password,
        termsAccepted,
        aiConsent,
        openSignupEnabled ? name.trim() : undefined,
        inviteSecret ?? undefined,
      );
      if (result.verificationRequired) {
        // Reason: open-signup needs an email click-through before login;
        // swap the form for a 'check your email' confirmation screen.
        setPendingEmail(result.email);
        return;
      }
      const next = getSafeNext();
      navigate(next || '/home');
    } catch (error: any) {
      setLocalError(error.message);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setResendError(null);
    setResendStatus('sending');
    try {
      await axios.post(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        email: pendingEmail,
      });
      setResendStatus('sent');
    } catch (err: any) {
      setResendStatus('idle');
      setResendError('Could not resend right now. Please try again in a moment.');
    }
  };

  const displayError = localError || error;

  if (pendingEmail) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          height: '100dvh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          alignItems: 'flex-start',
          py: { xs: 6, sm: 10 },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography
            component="h1"
            sx={{
              fontFamily:
                'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontWeight: 700,
              fontSize: { xs: 36, sm: 44 },
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              mb: 2,
            }}
          >
            Check your email
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We&rsquo;ve sent a confirmation link to{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {pendingEmail}
            </Box>
            . Click the link in that email to finish creating your account.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', mb: 3 }}>
            Don&rsquo;t forget to check your spam or junk folder if you can&rsquo;t see it.
          </Typography>

          {resendStatus === 'sent' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              If that email belongs to an unverified account, a fresh
              confirmation link is on its way.
            </Alert>
          )}
          {resendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resendError}
            </Alert>
          )}

          <Button
            onClick={handleResend}
            fullWidth
            variant="outlined"
            disabled={resendStatus === 'sending'}
            sx={{ py: 1.5, mb: 2 }}
          >
            {resendStatus === 'sending' ? (
              <CircularProgress size={20} />
            ) : (
              'Resend confirmation email'
            )}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                Back to{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Log in
                </Box>
              </Typography>
            </Link>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        // Reason: index.css locks html/body/#root to overflow:hidden,
        // so each page must own its own scroll container.
        height: '100dvh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        alignItems: 'flex-start',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily:
                'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontWeight: 700,
              fontSize: { xs: 44, sm: 50 },
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
            }}
          >
            Sign up
          </Typography>
          <Box
            aria-label="Jeff Says mark"
            sx={{
              width: 44,
              height: 44,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 0.5,
              mt: 1,
            }}
          >
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'primary.main' }} />
          </Box>
        </Box>

        {displayError && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {displayError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          {openSignupEnabled && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
                Name
              </Typography>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                autoComplete="name"
                autoFocus
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                inputProps={{ 'aria-label': 'Name' }}
              />
            </Box>
          )}

          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              Email
            </Typography>
            <TextField
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus={!openSignupEnabled}
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Email' }}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              Password
            </Typography>
            <PasswordTextField
              required
              fullWidth
              name="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Password' }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              Confirm password
            </Typography>
            <PasswordTextField
              required
              fullWidth
              name="confirmPassword"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Confirm password' }}
            />
          </Box>

          <Box
            sx={{
              border: '1px solid #D9D9D9',
              borderRadius: '12px',
              backgroundColor: '#F7F7F7',
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Important before sign-up
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              Jeff Says needs your agreement to our Terms of Use and Privacy
              Policy, and your consent for AI processing of your conversations
              to provide support.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Box
                  component="a"
                  href="https://jeffsays.com/terms-of-use.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'text.primary', fontWeight: 600 }}
                >
                  Terms of Use
                </Box>{' '}
                and{' '}
                <Box
                  component="a"
                  href="https://jeffsays.com/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'text.primary', fontWeight: 600 }}
                >
                  Privacy Policy
                </Box>
              </Typography>
            }
            sx={{ mb: 1.5, alignItems: 'center' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={aiConsent}
                onChange={(e) => setAiConsent(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="body2">
                I consent to my chat messages, contextual metadata, and
                relevant conversation context being transmitted to OpenAI to
                generate responses and to perform conversation analysis and
                summarisation. We do not opt in to OpenAI using our API data
                for model training or product improvement.
              </Typography>
            }
            sx={{ mb: 2.5, alignItems: 'flex-start' }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ py: 1.75, mb: 3 }}
            disabled={loading || !termsAccepted || !aiConsent}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign up'}
          </Button>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Link
              to={`/login${location.search}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                Already have an account?{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Log in
                </Box>
              </Typography>
            </Link>
          </Box>

          <Box
            aria-label="Mascot placeholder"
            sx={{
              width: '100%',
              height: 280,
              display: { xs: 'block', sm: 'none' },
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default RegisterPage;
